import type { LayerGroup, Map as LeafletMap } from "leaflet";
import { GATEWAY_MAP_SIZE, gatewayWorldToVulnonaMap } from "@isle/domain";
import { GATEWAY_BASEMAP_URL } from "./live-map.js";
import type { CustomAiMapSettings } from "./types.js";

let activeMap: LeafletMap | undefined;
let actorLayer: LayerGroup | undefined;
let activeContainer: HTMLElement | undefined;
let activeActors: CustomAiMapSettings["actors"] = [];
let savedView: { center: import("leaflet").LatLng; zoom: number } | undefined;

const speciesColours: Record<string, string> = {
  Carnotaurus: "#d66d57",
  Dryosaurus: "#6ecf91",
  Diabloceratops: "#e39b3a",
  Gallimimus: "#d8c66d",
  Omniraptor: "#b87bd6",
  Tenontosaurus: "#65b8c8"
};

export async function updateCustomAiMap(settings: CustomAiMapSettings): Promise<void> {
  const container = document.querySelector<HTMLElement>("#customAiMap");
  if (!container) return;
  const leafletModule = await import("leaflet");
  const L = (leafletModule as { default?: typeof import("leaflet") }).default ?? leafletModule;
  if (!activeMap || activeContainer !== container) {
    disposeCustomAiMap();
    container.replaceChildren();
    const bounds = L.latLngBounds([[0, 0], [GATEWAY_MAP_SIZE, GATEWAY_MAP_SIZE]]);
    activeMap = L.map(container, {
      crs: L.CRS.Simple,
      minZoom: -14,
      maxZoom: 4,
      zoomSnap: 0.25,
      zoomDelta: 0.5,
      wheelDebounceTime: 25,
      wheelPxPerZoomLevel: 80,
      zoomAnimation: true,
      fadeAnimation: true,
      maxBounds: bounds.pad(0.12),
      maxBoundsViscosity: 0.9,
      attributionControl: false
    });
    activeContainer = container;
    L.imageOverlay(GATEWAY_BASEMAP_URL, bounds, { interactive: false }).addTo(activeMap);
    actorLayer = L.layerGroup().addTo(activeMap);
    const overviewZoom = activeMap.getBoundsZoom(bounds, false, L.point(24, 24));
    activeMap.setMinZoom(overviewZoom);
    activeMap.setMaxZoom(overviewZoom + 8);
    if (savedView) activeMap.setView(savedView.center, clamp(savedView.zoom, overviewZoom, overviewZoom + 8), { animate: false });
    else activeMap.fitBounds(bounds, { animate: false, padding: [24, 24] });
  }

  activeActors = settings.actors;
  actorLayer?.clearLayers();
  for (const actor of settings.actors) {
    const point = gatewayWorldToVulnonaMap(actor.worldY, actor.worldX);
    const colour = speciesColours[actor.species] ?? "#d5d6c8";
    const marker = L.circleMarker([point.lat, point.lng], {
      radius: actor.species === "Diabloceratops" ? 7 : actor.species === "Tenontosaurus" ? 6 : 5,
      color: actor.status === "orphaned" ? "#e16654" : "#eef6e9",
      weight: actor.status === "orphaned" ? 3 : 1,
      fillColor: colour,
      fillOpacity: 0.94,
      className: actor.status === "orphaned" ? "custom-ai-marker is-orphaned" : "custom-ai-marker"
    }).addTo(actorLayer!);
    const tooltip = document.createElement("div");
    const title = document.createElement("strong");
    title.textContent = actor.species;
    const details = document.createElement("span");
    details.textContent = actor.status;
    const position = document.createElement("small");
    position.textContent = `${actor.slotId} // X ${Math.round(actor.worldX).toLocaleString()} // Y ${Math.round(actor.worldY).toLocaleString()} // Z ${Math.round(actor.worldZ).toLocaleString()}`;
    tooltip.append(title, details, position);
    marker.bindTooltip(tooltip, { direction: "top", className: "custom-ai-tooltip", opacity: 1 });
  }
  updateSummary(settings);
  activeMap?.invalidateSize();
}

export function fitCustomAiMap(): void {
  if (!activeMap || activeActors.length === 0) return;
  const points = activeActors.map((actor) => {
    const point = gatewayWorldToVulnonaMap(actor.worldY, actor.worldX);
    return [point.lat, point.lng] as [number, number];
  });
  void import("leaflet").then((leafletModule) => {
    const L = (leafletModule as { default?: typeof import("leaflet") }).default ?? leafletModule;
    activeMap?.fitBounds(L.latLngBounds(points), { animate: true, padding: [50, 50], maxZoom: activeMap.getMinZoom() + 4 });
  });
}

export function markCustomAiMapUnavailable(message: string): void {
  const summary = document.querySelector<HTMLElement>("#customAiMapSummary");
  if (!summary) return;
  summary.classList.add("is-stale");
  summary.innerHTML = `<span>Tracker unavailable</span><small>${escapeHtml(message)}</small>`;
}

export function disposeCustomAiMap(): void {
  actorLayer = undefined;
  activeActors = [];
  activeContainer = undefined;
  if (activeMap) {
    savedView = { center: activeMap.getCenter(), zoom: activeMap.getZoom() };
    activeMap.off();
    activeMap.remove();
  }
  activeMap = undefined;
}

function updateSummary(settings: CustomAiMapSettings): void {
  const summary = document.querySelector<HTMLElement>("#customAiMapSummary");
  if (!summary) return;
  const counts = new Map<string, number>();
  for (const actor of settings.actors) counts.set(actor.species, (counts.get(actor.species) ?? 0) + 1);
  const breakdown = [...counts.entries()].sort(([left], [right]) => left.localeCompare(right))
    .map(([species, count]) => `${count} ${species}`).join(" // ") || "No exact-owned AI currently alive";
  summary.classList.toggle("is-stale", settings.stale);
  summary.innerHTML = `<span><i class="custom-ai-map-pulse" aria-hidden="true"></i>${settings.actors.length} exact-owned AI across active player areas</span><small>${escapeHtml(breakdown)} // ${settings.stale ? "source stale" : `updated ${formatTime(settings.sourceCapturedAt)}`}</small>`;
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "unknown";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]!);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
